import React, { useState, useEffect } from "react";
import moment from "moment";
import * as yup from "yup";
import {
  Modal,
  Button,
  Form,
  DatePicker,
  Select,
  Row,
  Col,
  Radio,
  ValidationSummary,
} from "@countysuite/ui";
import { DropdownCategory } from "types/CivilTypes";
import {
  LackOfProsecutionCaseSearchResult,
  LackOfProsecutionCaseContact,
} from "../LackOfProsecutionTypes";

export interface LackOfProsecutionLettersModalProps {
  show: boolean;
  onHide: () => void;
  generateLetters: (
    selectedCaseID: number | undefined,
    letterId: number,
    deadlineDate: string,
    notificationDate: string,
    signatureId: number,
    letterIds: number[],
    pretrialDate: string
  ) => void;
  undoLetters: (
    selectedCaseID: number | undefined,
    letterId: number,
    letterIds: number[]
  ) => void;
  combineLetters: (
    letterId: number,
    deadlineDate: string,
    notificationDate: string,
    signatureId: number,
    letterIds: number[],
    pretrialDate: string
  ) => void;
  letterCategories: DropdownCategory[];
  caseResult?: LackOfProsecutionCaseSearchResult;
  signatures: DropdownCategory[];
  defaultSignature: number;
  hideDeadlineDate: boolean;
  lettersIds: number[];
  showPretrialDatePicker: boolean;
}

enum LetterActions {
  None = "",
  CreateAndAttach = "LackOfProsecution/GenerateLetters",
  CombineAll = "LackOfProsecution/GenerateCombinedPDF",
  UndoLetters = "LackOfProsecution/UndoLetters",
}

export const LackOfProsecutionLettersModal: React.FC<LackOfProsecutionLettersModalProps> = (
  props
) => {
  const { caseResult, defaultSignature } = props;

  const [letterAction, setLetterAction] = useState<LetterActions>(
    LetterActions.CreateAndAttach
  );
  const [letterID, setLetterID] = useState(0);
  const [
    notificationDate,
    setNotificationDate,
  ] = useState<moment.Moment | null>(moment());
  const [deadlineDate, setDeadlineDate] = useState<moment.Moment | null>(
    setDeadlineDateDefault()
  );
  const [signatureID, setSignature] = useState(defaultSignature);
  const [
    pretrialConferenceDate,
    setPretrialConferenceDate,
  ] = useState<moment.Moment | null>(moment().add(30, "days"));

  useEffect(() => {
    setSignature(defaultSignature);
  }, [defaultSignature]);

  useEffect(() => {
    setPretrialConferenceDate(pretrialConferenceDate);
  }, [pretrialConferenceDate]);

  function resetState() {
    setLetterAction(LetterActions.CreateAndAttach);
    setLetterID(0);
    setNotificationDate(moment());
    setDeadlineDate(moment().add(30, "days"));
    setSignature(props.defaultSignature);
    setPretrialConferenceDate(moment().add(30, "days"));
  }

  function setDeadlineDateDefault() {
    let deadlineDate = moment().add(30, "days");
    deadlineDate =
      deadlineDate.isoWeekday() === 6
        ? deadlineDate.add(2, "days")
        : deadlineDate;
    deadlineDate =
      deadlineDate.isoWeekday() === 7
        ? deadlineDate.add(1, "days")
        : deadlineDate;
    return deadlineDate;
  }

  const validationSchema = yup.object().shape({
    letterAction: yup.string().min(1, "Please choose a letter action."),
    letterID: yup
      .number()
      .min(
        1,
        "Please choose the type of letters you are trying to generate docket entries for."
      ),
    notificationDate: yup
      .object()
      .nullable()
      .required("Please choose a notification date for the letters."),
    deadlineDate: yup
      .object()
      .nullable()
      .required("Please choose a deadline date for the letters."),
  });

  function handleHide() {
    resetState();
    props.onHide();
  }

  function handleSubmit(e: any) {
    let selectedCaseID = caseResult ? caseResult.caseID : undefined;
    let deadline = deadlineDate ? deadlineDate.toISOString() : "";
    let notification = notificationDate ? notificationDate.toISOString() : "";
    let pretrial = pretrialConferenceDate
      ? pretrialConferenceDate.toISOString()
      : "";

    switch (letterAction) {
      case LetterActions.CreateAndAttach:
        props.generateLetters(
          selectedCaseID,
          letterID,
          deadline,
          notification,
          signatureID,
          props.lettersIds,
          pretrial
        );
        break;
      case LetterActions.CombineAll:
        props.combineLetters(
          letterID,
          deadline,
          notification,
          signatureID,
          props.lettersIds,
          pretrial
        );
        break;
      case LetterActions.UndoLetters:
        props.undoLetters(selectedCaseID, letterID, props.lettersIds);
        break;
      default:
        console.error(
          "Invalid letter action type, cannot determine what API call to make!"
        );
        return;
    }

    //reset state
    resetState();
    props.onHide();
  }

  let letterRecipients: LackOfProsecutionCaseContact[] = [];
  if (caseResult && caseResult.letterRecipients.length > 0) {
    letterRecipients = caseResult.letterRecipients;
  }

  const affectedCases = caseResult
    ? `Case Number ${caseResult.caseNumber}`
    : `All Cases`;

  const deadlineDatePicker = (
    <DatePicker
      label="Deadline Date"
      id="deadlineDate"
      date={deadlineDate}
      onDateChange={(date) => setDeadlineDate(date)}
    />
  );

  const pretrialDatePicker = (
    <Col xs={12} md={4}>
      <DatePicker
        label="Pretrial Conference Date"
        id="pretrialConferenceDate"
        date={pretrialConferenceDate}
        onDateChange={(date) => setPretrialConferenceDate(date)}
      />
    </Col>
  );

  return (
    <Form
      values={{ letterID, notificationDate, deadlineDate, letterAction }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      showErrors={false}
    >
      {({ handleSubmit, errorSummary }) => (
        <Modal
          title={`Generate Letters and Dockets for ${affectedCases}`}
          show={props.show}
          size="medium"
          onHide={handleHide}
          buttons={
            <>
              <Button onClick={handleSubmit}>Generate</Button>
              <Button onClick={handleHide}>Close</Button>
            </>
          }
        >
          <ValidationSummary errors={errorSummary.errors} />

          <div style={{ margin: "0 1rem" }}>
            {letterRecipients.length > 0 && (
              <Row style={{ marginBottom: "2em" }}>
                <Col xs={12}>
                  <h4>Letter Recipients</h4>
                  <ol className="lop-table-list">
                    {letterRecipients.map((c) => (
                      <li key={c.contactID}>
                        <span>
                          {c.contactName} ({c.contactType})
                        </span>
                        <div>{c.contactAddress || "No Address"}</div>
                      </li>
                    ))}
                  </ol>
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={12} md={8}>
                <Select
                  options={props.letterCategories}
                  label="Select the type of letter"
                  name="letterType"
                  onChange={(e: any) =>
                    setLetterID(Number(e.target.value) || 0)
                  }
                  value={letterID}
                />

                <Select
                  options={props.signatures}
                  label="Signature"
                  name="signature"
                  onChange={(e: any) =>
                    setSignature(Number(e.target.value) || 1)
                  }
                  value={signatureID}
                />
                {!caseResult && (
                  <>
                    <Radio
                      name="letterGroup"
                      onChange={(e) =>
                        setLetterAction(LetterActions.CreateAndAttach)
                      }
                      defaultChecked
                    >
                      Create Docket Entries &amp; Letters
                    </Radio>
                    <Radio
                      name="letterGroup"
                      onChange={(e) =>
                        setLetterAction(LetterActions.CombineAll)
                      }
                    >
                      Combine All Letters As Single PDF
                    </Radio>
                  </>
                )}
                <>
                  <Radio
                    name="letterGroup"
                    onChange={(e) => {
                      let selectedCaseID = caseResult ? caseResult.caseID : undefined;
                      setLetterAction(LetterActions.UndoLetters)
                    }
                    }
                  >
                    Undo Docket Entries of this Type
                  </Radio>
                </>
              </Col>
              <Col xs={12} md={4}>
                <div className="pull-right">
                  <DatePicker
                    label="Date of Notification"
                    id="notificationDate"
                    date={notificationDate}
                    onDateChange={(date) => setNotificationDate(date)}
                  />
                  {props.hideDeadlineDate ? null : deadlineDatePicker}
                </div>
              </Col>
              {!props.showPretrialDatePicker ? null : pretrialDatePicker}
            </Row>
          </div>
        </Modal>
      )}
    </Form>
  );
};

(LackOfProsecutionLettersModal as any).displayName =
  "LackOfProsecutionLettersModal";
